import { useApi } from 'contexts/Api/Api';
import { useContent } from 'contexts/Content/Content';
import { getUrl } from 'helpers';
import { useCallback, useEffect, useRef, useState } from 'react';
import AuthModalButton from '../AuthModalButton/AuthModalButton';
import {
    Container,
    Content,
    Logo,
    LogoMobile,
    LogoPicture,
    LogoWrap,
    PortionMarking,
    Tagline,
    Text,
    Title,
    UserActionLink,
    UserActions,
    UserContainer,
    UserImage,
    UserName,
    UserPicture,
    UserProfile,
    UserProfileMenu,
    UserProfileMenuButton,
    UserProfileMenuLink,
    Wrap,
} from './Header.styled';

const MENU_FIRST_FOCUSABLE = '[data-testid="header-my-profile"], [data-testid="header-logout"]';

export default function Header({ children, style, visible = true }: HeaderProps) {
    const [openProfileMenu, setOpenProfileMenu] = useState(false);
    const api = useApi();
    const content = useContent();
    const userContainerRef = useRef<HTMLDivElement>(null);

    const { background, backgroundMobile, foreground, logo, logoLink, logoMobile, tagline, textOffset, title } =
        content.header;
    const { portionMarking } = content.general;

    const handleClick = useCallback(
        (e: MouseEvent) => {
            if (openProfileMenu && userContainerRef.current && !userContainerRef.current.contains(e.target as Node)) {
                setOpenProfileMenu(false);
            }
        },
        [openProfileMenu, userContainerRef]
    );

    const menuRef = useRef<HTMLDivElement>(null);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setOpenProfileMenu(false);
            }
            if (!openProfileMenu || !menuRef.current) {
                return;
            }
            const focusable = menuRef.current.querySelectorAll<HTMLElement>(MENU_FIRST_FOCUSABLE);
            if (e.key === 'ArrowDown' && document.activeElement === userContainerRef.current?.querySelector('button')) {
                e.preventDefault();
                (focusable[0] as HTMLElement)?.focus();
            }
        },
        [openProfileMenu]
    );

    useEffect(() => {
        window.addEventListener('click', handleClick);
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('click', handleClick);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleClick, handleKeyDown]);

    useEffect(() => {
        if (openProfileMenu && menuRef.current) {
            const first = menuRef.current.querySelector<HTMLElement>(MENU_FIRST_FOCUSABLE);
            first?.focus();
        }
    }, [openProfileMenu]);

    function handleLogout() {
        void api.logout();
        setOpenProfileMenu(false);
    }

    return (
        <Wrap style={style}>
            {portionMarking && <PortionMarking>{portionMarking}</PortionMarking>}
            {visible && (
                <Container $background={background} $backgroundMobile={backgroundMobile || background}>
                    <LogoWrap>
                        <a aria-label="Home" data-testid="header-logo-link" href={logoLink || '/'}>
                            <LogoPicture>
                                <LogoMobile alt="" src={getUrl(logoMobile || logo)} aria-hidden />
                                <Logo alt="Kromatic logo" src={getUrl(logo)} />
                            </LogoPicture>
                        </a>
                    </LogoWrap>
                    <Content $foreground={foreground}>
                        <Text href={getUrl('/')} $foreground={foreground} $offset={textOffset}>
                            <Title>{title}</Title>
                            {tagline && <Tagline>{tagline}</Tagline>}
                        </Text>
                        {children}
                        {api.authenticate && (
                            <>
                                {api.authenticated ? (
                                    <UserActions>
                                        <UserActionLink aria-label="My Kit" data-testid="header-my-kit" href="/mykit">
                                            My Kit
                                        </UserActionLink>
                                        <UserContainer ref={userContainerRef}>
                                            <UserProfile
                                                aria-expanded={openProfileMenu}
                                                aria-haspopup="true"
                                                aria-label="User menu"
                                                data-testid="header-user-menu-button"
                                                onClick={() => setOpenProfileMenu(!openProfileMenu)}
                                                type="button"
                                            >
                                                <UserName>{api.userInfo?.first_name || 'Account'}</UserName>
                                                <UserPicture>
                                                    <UserImage
                                                        alt=""
                                                        aria-hidden
                                                        src={api.userInfo?.avatar || getUrl('/assets/user-icon.svg')}
                                                    />
                                                </UserPicture>
                                            </UserProfile>
                                            {openProfileMenu && (
                                                <UserProfileMenu
                                                    aria-label="User menu"
                                                    ref={menuRef}
                                                    role="menu"
                                                    data-testid="header-user-menu"
                                                >
                                                    <UserProfileMenuLink
                                                        href="/user"
                                                        role="menuitem"
                                                        data-testid="header-my-profile"
                                                    >
                                                        My Profile
                                                    </UserProfileMenuLink>
                                                    <UserProfileMenuButton
                                                        onClick={handleLogout}
                                                        role="menuitem"
                                                        data-testid="header-logout"
                                                        type="button"
                                                    >
                                                        Log Out
                                                    </UserProfileMenuButton>
                                                </UserProfileMenu>
                                            )}
                                        </UserContainer>
                                    </UserActions>
                                ) : (
                                    <UserActions>
                                        <AuthModalButton
                                            aria-label="Log in"
                                            data-testid="header-login-button"
                                            variant="white"
                                            onClick={api.showAuthModal}
                                        >
                                            Log in
                                        </AuthModalButton>
                                        <AuthModalButton
                                            aria-label="Join now"
                                            data-testid="header-join-button"
                                            onClick={() => api.showAuthModal('registration')}
                                        >
                                            Join Now
                                        </AuthModalButton>
                                    </UserActions>
                                )}
                            </>
                        )}
                    </Content>
                </Container>
            )}
        </Wrap>
    );
}
